import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useForm, FormProvider, useFieldArray } from 'react-hook-form';
import toast from 'react-hot-toast';
import { parseISO } from 'date-fns';
import { Loader2 } from 'lucide-react';
import { supabase } from '@/lib/supabase';
import { useAuth } from '@/context/AuthContext';
import { useFamily } from '@/context/FamilyContext';
import { TopBar } from '@/components/TopBar';
import { ReminderFormRow } from '@/components/ReminderForm';
import type { Item, ItemType, ReminderFormData, RepeatUnit } from '@/types';
import { VEHICLE_REMINDER_PRESETS, APPLIANCE_REMINDER_PRESETS } from '@/types';
import { toISODate, computeNextDueDate } from '@/lib/dateUtils';

interface FormValues {
  type: ItemType;
  name: string;
  brand: string;
  model: string;
  serial_or_reg_no: string;
  purchase_date: string;
  store: string;
  price: string;
  warranty_end: string;
  notes: string;
  reminders: ReminderFormData[];
}

export function AddEditItem() {
  const { id } = useParams<{ id: string }>();
  const isEdit = Boolean(id);
  const navigate = useNavigate();
  const { user } = useAuth();
  const { family } = useFamily();
  const [saving, setSaving] = useState(false);
  const [loadingItem, setLoadingItem] = useState(isEdit);

  const methods = useForm<FormValues>({
    defaultValues: {
      type: 'vehicle',
      name: '', brand: '', model: '', serial_or_reg_no: '',
      purchase_date: '', store: '', price: '', warranty_end: '', notes: '',
      reminders: []
    }
  });

  const { register, control, handleSubmit, watch, setValue, reset } = methods;
  const { fields, append, remove, replace } = useFieldArray({ control, name: 'reminders' });
  const itemType = watch('type');
  const purchaseDate = watch('purchase_date');

  useEffect(() => {
    if (!isEdit || !id) return;
    (async () => {
      const { data: item } = await supabase.from('items').select('*').eq('id', id).maybeSingle();
      if (!item) { navigate('/items'); return; }
      const { data: rems } = await supabase.from('reminders').select('*').eq('item_id', id).order('created_at');
      const i = item as Item;
      reset({
        type: i.type,
        name: i.name,
        brand: i.brand ?? '',
        model: i.model ?? '',
        serial_or_reg_no: i.serial_or_reg_no ?? '',
        purchase_date: i.purchase_date ?? '',
        store: i.store ?? '',
        price: i.price != null ? String(i.price) : '',
        warranty_end: i.warranty_end ?? '',
        notes: i.notes ?? '',
        reminders: (rems ?? []).map((r) => ({
          title: r.title,
          repeat_every: r.repeat_every,
          repeat_unit: r.repeat_unit as RepeatUnit,
          remind_before_days: r.remind_before_days,
          start_date: r.start_date,
          one_time: r.one_time
        }))
      });
      setLoadingItem(false);
    })();
  }, [id, isEdit, navigate, reset]);

  const addPreset = (preset: ReminderFormData) => {
    const startDate = purchaseDate || toISODate(new Date());
    append({ ...preset, start_date: startDate });
  };

  const addCustom = () => {
    const startDate = purchaseDate || toISODate(new Date());
    append({ title: '', repeat_every: 1, repeat_unit: 'months', remind_before_days: 7, start_date: startDate, one_time: false });
  };

  const onSubmit = async (values: FormValues) => {
    if (!family || !user) return;
    setSaving(true);

    const itemPayload = {
      family_id: family.id,
      type: values.type,
      name: values.name.trim(),
      brand: values.brand.trim() || null,
      model: values.model.trim() || null,
      serial_or_reg_no: values.serial_or_reg_no.trim() || null,
      purchase_date: values.purchase_date || null,
      store: values.store.trim() || null,
      price: values.price ? parseFloat(values.price) : null,
      warranty_end: values.warranty_end || null,
      notes: values.notes.trim() || null,
      created_by: user.id
    };

    let itemId = id ?? '';

    if (isEdit) {
      const { error } = await supabase.from('items').update(itemPayload).eq('id', id!);
      if (error) { toast.error(error.message); setSaving(false); return; }
      // Delete old reminders and re-insert
      await supabase.from('reminders').delete().eq('item_id', id!);
    } else {
      const { data, error } = await supabase.from('items').insert(itemPayload).select().single();
      if (error) { toast.error(error.message); setSaving(false); return; }
      itemId = data.id;
    }

    // Insert reminders
    if (values.reminders.length > 0) {
      const remPayloads = values.reminders.map((r) => {
        const base = parseISO(r.start_date);
        const nextDue = r.one_time
          ? base
          : computeNextDueDate(base, Number(r.repeat_every), r.repeat_unit);
        return {
          item_id: itemId,
          family_id: family.id,
          title: r.title.trim(),
          repeat_every: Number(r.repeat_every),
          repeat_unit: r.repeat_unit,
          remind_before_days: Number(r.remind_before_days),
          start_date: r.start_date,
          next_due_date: toISODate(nextDue),
          is_active: true,
          one_time: r.one_time
        };
      });

      const { error } = await supabase.from('reminders').insert(remPayloads);
      if (error) { toast.error(error.message); setSaving(false); return; }
    }

    setSaving(false);
    toast.success(isEdit ? 'Item updated!' : 'Item added!');
    navigate(isEdit ? `/items/${id}` : `/items/${itemId}`);
  };

  const inputCls = 'w-full px-3 py-2.5 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-brand-500 bg-white';
  const labelCls = 'block text-sm font-medium text-gray-700 mb-1';
  const presets = itemType === 'vehicle' ? VEHICLE_REMINDER_PRESETS : APPLIANCE_REMINDER_PRESETS;

  if (loadingItem) return <div className="flex items-center justify-center h-64 text-gray-400">Loading…</div>;

  return (
    <div>
      <TopBar title={isEdit ? 'Edit Item' : 'Add Item'} back={isEdit ? `/items/${id}` : '/items'} />
      <FormProvider {...methods}>
        <form onSubmit={handleSubmit(onSubmit)} className="px-4 py-4 space-y-5 pb-8">

          {/* Type */}
          <div>
            <label className={labelCls}>Category</label>
            <div className="grid grid-cols-2 gap-3">
              {(['vehicle', 'appliance'] as ItemType[]).map((t) => (
                <button
                  key={t}
                  type="button"
                  onClick={() => { setValue('type', t); replace([]); }}
                  className={`py-2.5 rounded-xl text-sm font-medium border-2 transition-colors ${
                    itemType === t ? 'border-brand-500 bg-brand-50 text-brand-700' : 'border-gray-200 text-gray-500 hover:border-gray-300'
                  }`}
                >
                  {t === 'vehicle' ? '🚗 Vehicle' : '🏠 Appliance'}
                </button>
              ))}
            </div>
          </div>

          {/* Basic Info */}
          <div className="space-y-3">
            <h2 className="text-xs font-semibold text-gray-400 uppercase tracking-wider">Basic Info</h2>
            <div>
              <label className={labelCls}>Name <span className="text-red-400">*</span></label>
              <input {...register('name', { required: true })} placeholder="e.g. Toyota Innova / Samsung Fridge" className={inputCls} />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className={labelCls}>Brand</label>
                <input {...register('brand')} placeholder="Toyota" className={inputCls} />
              </div>
              <div>
                <label className={labelCls}>Model</label>
                <input {...register('model')} placeholder="Innova Crysta" className={inputCls} />
              </div>
            </div>
            <div>
              <label className={labelCls}>{itemType === 'vehicle' ? 'Registration No.' : 'Serial No.'}</label>
              <input {...register('serial_or_reg_no')} placeholder={itemType === 'vehicle' ? 'MH 01 AB 1234' : 'SN-123456'} className={inputCls} />
            </div>
          </div>

          {/* Purchase Info */}
          <div className="space-y-3">
            <h2 className="text-xs font-semibold text-gray-400 uppercase tracking-wider">Purchase Info</h2>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className={labelCls}>Purchase date</label>
                <input type="date" {...register('purchase_date')} className={inputCls} />
              </div>
              <div>
                <label className={labelCls}>Price (₹)</label>
                <input type="number" step="0.01" {...register('price')} placeholder="0.00" className={inputCls} />
              </div>
            </div>
            <div>
              <label className={labelCls}>Store / Dealer</label>
              <input {...register('store')} placeholder="e.g. Maruti Suzuki, Amazon" className={inputCls} />
            </div>
            <div>
              <label className={labelCls}>Warranty end</label>
              <input type="date" {...register('warranty_end')} className={inputCls} />
            </div>
            <div>
              <label className={labelCls}>Notes</label>
              <textarea {...register('notes')} rows={2} placeholder="Any additional info…" className={`${inputCls} resize-none`} />
            </div>
          </div>

          {/* Reminders */}
          <div className="space-y-3">
            <h2 className="text-xs font-semibold text-gray-400 uppercase tracking-wider">Reminders</h2>

            {/* Presets */}
            <div>
              <p className="text-xs text-gray-500 mb-2">Quick add preset:</p>
              <div className="flex flex-wrap gap-2">
                {presets.map((p) => (
                  <button
                    key={p.title}
                    type="button"
                    onClick={() => addPreset(p)}
                    className="text-xs px-3 py-1.5 bg-brand-50 text-brand-700 rounded-lg hover:bg-brand-100 transition-colors border border-brand-200"
                  >
                    + {p.title}
                  </button>
                ))}
                <button
                  type="button"
                  onClick={addCustom}
                  className="text-xs px-3 py-1.5 bg-gray-100 text-gray-600 rounded-lg hover:bg-gray-200 transition-colors"
                >
                  + Custom
                </button>
              </div>
            </div>

            {fields.map((field, index) => (
              <ReminderFormRow key={field.id} index={index} onRemove={() => remove(index)} />
            ))}
          </div>

          <button
            type="submit"
            disabled={saving}
            className="w-full flex items-center justify-center gap-2 py-3 bg-brand-600 text-white rounded-xl font-medium hover:bg-brand-700 disabled:opacity-50 transition-colors"
          >
            {saving && <Loader2 className="w-4 h-4 animate-spin" />}
            {saving ? 'Saving…' : isEdit ? 'Update item' : 'Add item'}
          </button>
        </form>
      </FormProvider>
    </div>
  );
}
